/**
 * Freshness rules for generation progress that arrives over two channels.
 *
 * Progress is written to the plan doc and reaches the client both through
 * Firestore listeners (frequent, fresh) and through REST refetches of the
 * assembled plan (which capture the doc as of when the read started). A
 * refetch that resolves after the listener has already rendered newer
 * progress would silently rewind it, which shows as the bar briefly moving
 * backwards and the stage line flickering. The backend stamps every progress
 * write with updatedAt precisely so staleness is detectable; these helpers
 * normalize it across channel encodings and keep whichever copy is newer.
 */

/**
 * updatedAt as milliseconds, whatever shape the channel delivered it in:
 * a Firestore client Timestamp (has toMillis), the admin SDK's serialized
 * form ({_seconds}/{seconds}), or an ISO string. Unknown shapes rank
 * oldest, so a copy with a readable timestamp always beats one without.
 */
export const progressUpdatedAtMillis = (value: unknown): number => {
  if (!value) return 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object') {
    const candidate = value as {
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };
    if (typeof candidate.toMillis === 'function') return candidate.toMillis();
    const seconds = candidate.seconds ?? candidate._seconds;
    if (typeof seconds === 'number') {
      const nanos = candidate.nanoseconds ?? candidate._nanoseconds ?? 0;
      return seconds * 1000 + Math.round(nanos / 1e6);
    }
  }
  return 0;
};

/**
 * The newer of two progress snapshots. Ties keep `current`: equal
 * timestamps mean the same write seen twice, and keeping the object already
 * in state avoids a pointless re-render.
 */
export const freshestProgress = <T extends { updatedAt?: unknown }>(
  current: T | undefined,
  incoming: T | undefined
): T | undefined => {
  if (!current) return incoming;
  if (!incoming) return current;
  return progressUpdatedAtMillis(incoming.updatedAt) >
    progressUpdatedAtMillis(current.updatedAt)
    ? incoming
    : current;
};

type PlanLike = {
  id: string;
  generationStatus: { status: string };
  generationProgress?: { updatedAt?: unknown };
};

/**
 * Overlays the freshest known generationProgress onto REST-fetched plans.
 *
 * Only in-flight plans are touched: a terminal status means generation is
 * over and the progress field is gone from the doc, and a live entry left
 * over from the final in-flight snapshot must not resurrect it. Plans whose
 * REST copy is already the freshest are returned unchanged, identity
 * included.
 */
export const withFreshestGenerationProgress = <T extends PlanLike>(
  plans: T[],
  liveProgressById: ReadonlyMap<string, T['generationProgress']>
): T[] =>
  plans.map((plan) => {
    const status = plan.generationStatus.status;
    if (status !== 'pending' && status !== 'generating') return plan;

    const winner = freshestProgress(
      plan.generationProgress,
      liveProgressById.get(plan.id)
    );
    return winner === plan.generationProgress
      ? plan
      : { ...plan, generationProgress: winner };
  });
